import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef } from "react";
import Auth from "src/Auth";
import { useAutoSave } from "src/hooks/useAutoSave";
import { useStructuredOutlineStore } from "./StructuredOutlineStore";

export function useOutlineSettingsAutoSave({ pendingSettings, readOnly, restoring, setToast }) {
  const restoringProjects = useRef(new Set());
  const restoreGenerations = useRef({});
  const inFlight = useRef({});
  const saveSettings = useCallback(async (pending) => {
    const generations = { ...restoreGenerations.current };
    for (const [settingsProjectId, settings] of Object.entries(pending)) {
      if (restoringProjects.current.has(settingsProjectId)
        || generations[settingsProjectId] !== restoreGenerations.current[settingsProjectId]) continue;
      try {
        const payload = { projectId: settingsProjectId, settings };
        const request = Auth.fetchWithIdToken("/api/outline/settings", { ...payload, method: "PUT" });
        inFlight.current[settingsProjectId] = request;
        const result = await request;
        if (result.success) useStructuredOutlineStore.getState().acknowledgeSettings(payload);
      } catch (error) {
        Sentry.captureException(error, { tags: { feature: "structured_outline_settings_save" } });
        setToast("Could not save outline settings");
      } finally {
        delete inFlight.current[settingsProjectId];
      }
    }
  }, [setToast]);
  const { debouncedSave: updateSettings, handleBlur } = useAutoSave({
    saveFn: saveSettings,
    entity: pendingSettings,
    readOnly: readOnly || restoring,
  });
  useEffect(() => {
    if (!readOnly && !restoring && Object.keys(pendingSettings).length) updateSettings(pendingSettings);
  }, [pendingSettings, updateSettings, readOnly, restoring]);

  const withSettingsRestore = useCallback(async (projectId, restore) => {
    restoringProjects.current.add(projectId);
    restoreGenerations.current[projectId] = (restoreGenerations.current[projectId] || 0) + 1;
    updateSettings.cancel();
    try {
      // A settings request already sent must settle before the snapshot wins.
      await inFlight.current[projectId]?.catch(() => {});
      const result = await restore();
      useStructuredOutlineStore.getState().discardSettings(projectId);
      return result;
    } finally {
      restoringProjects.current.delete(projectId);
      const remaining = useStructuredOutlineStore.getState().pendingSettings;
      if (Object.keys(remaining).length) updateSettings(remaining);
    }
  }, [updateSettings]);

  return { handleBlur, withSettingsRestore };
}
